import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CustomizerSettingsService } from '../../../../customizer-settings/customizer-settings.service';
import { Country } from '../../../../models/domain/Country';
import { CountryService } from '../../../../services/country.service';
import { LoadingService } from '../../../../services/loading.service';
import { ObjectUtility } from '../../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../../validators/ValidationService';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-country-details',
  templateUrl: './country-details.component.html',
  styleUrl: './country-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule],
})

export class CountryDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() data!: Country;
  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public countryOriginal = new Country;
  public country: Country = new Country;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  public currentCountryId = 0;
  isSubmitted = false;

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private countryService: CountryService,
    private toasterService: ToastrService,
    private loadingService: LoadingService
  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    if (this.data) {
      this.currentCountryId = this.data.id ? this.data.id : 0;
      this.country = ObjectUtility.deepClone(this.data);
      this.countryOriginal = ObjectUtility.deepClone(this.data);
      this.setForm(this.country);
    }
    else {
      this.currentCountryId = 0;
      const newCountry = new Country();
      this.country = ObjectUtility.deepClone(newCountry);
      this.countryOriginal = ObjectUtility.deepClone(newCountry);
      this.setForm(this.country);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.country.name = value;
    });

     this.frmGroup.controls['codeControl'].valueChanges.subscribe(value => {
      this.country.code = value;
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get f() { return this.frmGroup.controls; }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      nameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      codeControl: new FormControl('', whitespaceValidator())
    })
  }

  private setForm(country: Country): void {
    if (country) {
      this.frmGroup.patchValue({
        nameControl: country.name,
        codeControl: country.code,
      });
    }
    this.titleText = `${this.currentCountryId === 0 ? "Add" : "Edit"} Country`;
    this.titleButton = `${this.currentCountryId === 0 ? "Create" : "Update"}`;
  }

  public onSave(): void {
    this.isSubmitted = true;
    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditCountry();
    }
  }

  private addEditCountry(): void {
    if (this.country?.id === 0) {
      this.countryService.create(this.country)
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.country = response?.body != null ? response?.body : new Country;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.countryService.update(this.country)
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.country = response?.body != null ? response?.body : new Country;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }


  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.country, this.countryOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['nameControl'].valid
    return isValid;
  }


}